import React, { useEffect, useState, useRef } from 'react';
import { FONT_FAMILY } from '../constants/font';

/**
 * UpdateBanner — detecta cuando hay un deploy nuevo y muestra una franja
 * naranja al user pidiendole recargar.
 *
 * Como funciona:
 *   1. Al montar, lee `/asset-manifest.json` (lo emite CRA en cada build) y
 *      guarda el hash del bundle main.js como "version actual".
 *   2. Cada 5 min hace fetch al manifest con cache-busting; si el hash de
 *      main.js cambio, hubo deploy nuevo en el server.
 *   3. Muestra banner con boton "Recargar" → window.location.reload().
 *
 * Por que no Service Worker: SW tiene gotchas en iOS Safari / webviews
 * embebidos (ej. WhatsApp browser). Polling es mas simple, sin riesgos
 * y suficiente para este caso (forzar refresh tras deploy).
 *
 * Por que asset-manifest.json y no /api/version: el manifest cambia con
 * CADA build del frontend (CRA regenera hashes). Si solo se redeployara
 * el backend pero no el frontend, los users no verian un banner falso.
 *
 * Skip en development: no tiene sentido pollear cuando estas en localhost.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos
const MANIFEST_PATH = '/asset-manifest.json';

async function fetchMainJsHash() {
  try {
    // cache-busting con timestamp para evitar que Cloudflare/browser cacheen
    const res = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const data = await res.json();
    // El bundle main lo extraemos del files map. CRA usa el path con hash:
    // "main.js": "/static/js/main.<hash>.js"
    return data?.files?.['main.js'] || null;
  } catch {
    return null;
  }
}

export default function UpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialHashRef = useRef(null);

  useEffect(() => {
    // Skip en development local — no hay deploys aqui
    if (typeof window === 'undefined') return undefined;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    // Setup inicial: capturar el hash del bundle actual (el que cargo esta sesion)
    fetchMainJsHash().then(hash => {
      if (cancelled) return;
      initialHashRef.current = hash;

      // Empezar a pollear solo si pudimos capturar el inicial
      if (hash) {
        intervalId = setInterval(async () => {
          if (cancelled || initialHashRef.current === null) return;
          const latest = await fetchMainJsHash();
          if (cancelled) return;
          // Si el hash del bundle cambio en el server, hubo deploy nuevo.
          // El bundle local sigue siendo el viejo hasta que el user recargue.
          if (latest && latest !== initialHashRef.current) {
            setUpdateAvailable(true);
            // Una vez detectado, cortar el polling — ya no necesitamos seguir
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        }, POLL_INTERVAL_MS);
      }
    });

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  if (!updateAvailable) return null;

  const handleReload = () => {
    // location.reload(true) es deprecated en navegadores modernos pero el
    // cache-busting natural del nuevo asset-manifest.json + los nuevos hashes
    // de chunks fuerza el bundle nuevo igual.
    window.location.reload();
  };

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#FF6B00',
        color: '#fff',
        padding: '12px 20px',
        borderRadius: 12,
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        zIndex: 10000,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: FONT_FAMILY,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <span style={{ fontSize: 16 }}>🔄</span>
      <span style={{ flex: 1 }}>
        Hay una versión nueva de la plataforma disponible.
      </span>
      <button
        onClick={handleReload}
        style={{
          background: '#fff',
          color: '#FF6B00',
          border: 'none',
          borderRadius: 8,
          padding: '8px 14px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: FONT_FAMILY,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
        }}
      >
        Recargar
      </button>
    </div>
  );
}
